import GoverningStandardProcessIcon from 'assets/images/ArtifactIcons/GoverningStandardProcess.svg';
import KeyProcessGxPIcon from 'assets/images/ArtifactIcons/keyProcessGxP.svg';
import KeyProcessBusinessControlledIcon from 'assets/images/ArtifactIcons/keyProcessBusinessControlled.svg';
import {Box, Grid, Link, Paper, Stack, Typography, useTheme} from '@mui/material';
import {IProcess, CardType, DocumentCategories, newDocumentCategories, Artifacts} from 'app/types/Shared';
import {ArtifactCard} from 'components/ArtifactCards/ArtifactCard';
import {ArtifactCards} from 'components/ArtifactCards/ArtifactCards';
import {Close} from '@mui/icons-material';
import {EmptyState} from 'components/EmptyState/EmptyState';
import IconKey from 'components/IconKey/IconKey';
import {Launch} from '@carbon/icons-react';

type DocsAndTemplateProps = {
  process: IProcess;
  storybook: boolean;
  ttdom?: boolean;
  search?: (query: string, filter?: DocumentCategories) => Promise<void>;
  viewAllCallback?: () => Promise<void>;
};

const DocAndTemplatesTab = ({process, storybook, ttdom}: DocsAndTemplateProps) => {
  const theme = useTheme();
  const {artifacts} = process;

  const makeUnique = (artifactArray: Artifacts[]) =>
    artifactArray.reduce((acc, curr) => {
      if (!acc.some((item) => item.artifact.id === curr.artifact.id)) {
        acc.push(curr);
      }
      return acc;
    }, [] as Artifacts[]);
  const governingStandardProcessDocs = makeUnique(
    process.artifacts?.filter((a) =>
      a.artifact.j_process_artifact
        ?.map(
          (jpa) =>
            jpa.document_category === newDocumentCategories.GoverningStandardProcess &&
            process.process_id === jpa.process.process_id,
        )
        .includes(true),
    ),
  );
  const keyProcessBusinessControlledDocs = makeUnique(
    process.artifacts?.filter((a) =>
      a.artifact.j_process_artifact
        ?.map(
          (jpa) =>
            jpa.document_category === newDocumentCategories.KeyProcessBusinessControlled &&
            process.process_id === jpa.process.process_id,
        )
        .includes(true),
    ),
  );
  const keyProcessGxPDocs = makeUnique(
    process.artifacts?.filter((a) =>
      a.artifact.j_process_artifact
        ?.map(
          (jpa) =>
            jpa.document_category === newDocumentCategories.KeyProcessGxP &&
            process.process_id === jpa.process.process_id,
        )
        .includes(true),
    ),
  );
  const referencedDocs = makeUnique(
    ttdom
      ? process.artifacts
      : process.artifacts?.filter((a) =>
          a.artifact.j_process_artifact
            ?.map(
              (jpa) =>
                jpa.document_category === newDocumentCategories.Referenced &&
                process.process_id === jpa.process.process_id,
            )
            .includes(true),
        ),
  );

  const getItemKey = () => {
    const iconKey: Array<{title: string; svg: string}> = [];
    const processDocuments = governingStandardProcessDocs
      .concat(keyProcessBusinessControlledDocs)
      .concat(keyProcessGxPDocs)
      .concat(referencedDocs);
    const artifactsCategory = Array.from(
      new Set(processDocuments?.flatMap((a) => a.artifact.j_process_artifact?.flatMap((jpa) => jpa.document_category))),
    );
    if (artifactsCategory?.includes(newDocumentCategories.GoverningStandardProcess)) {
      iconKey.push({title: newDocumentCategories.GoverningStandardProcess, svg: GoverningStandardProcessIcon});
    }
    if (artifactsCategory?.includes(newDocumentCategories.KeyProcessGxP)) {
      iconKey.push({title: newDocumentCategories.KeyProcessGxP, svg: KeyProcessGxPIcon});
    }
    if (artifactsCategory?.includes(newDocumentCategories.KeyProcessBusinessControlled)) {
      iconKey.push({
        title: newDocumentCategories.KeyProcessBusinessControlled,
        svg: KeyProcessBusinessControlledIcon,
      });
    }

    return iconKey;
  };

  return (
    <Paper
      sx={{
        px: 6,
        py: 2,
        backgroundColor: theme.palette.grey[100],
        borderTop: `1px solid ${theme.palette.grey[500]}`,
        minHeight: '75vh',
      }}
    >
      <Box pb={'40px'} pt='20px'>
        <Link
          color='primary'
          underline='hover'
          onClick={() =>
            window.open('https://mytakeda.sharepoint.com/sites/ELEVATEBCDR/CRO%20Managed/Forms/AllItems.aspx', '_blank')
          }
          rel='noopener noreferrer'
          sx={{cursor: 'pointer'}}
          display='flex'
          rowGap='10px'
        >
          <Stack direction='row' spacing='5px'>
            <Typography variant='h5' fontWeight={400}>
              CRO Managed Documents
            </Typography>{' '}
            <Launch color={theme.palette.primary.main} width='18px' height='18px' />
          </Stack>
        </Link>
      </Box>
      {artifacts?.length ? (
        <Grid container direction='row' spacing='20px' pb={12}>
          <Grid item xs={9.5} sm={8} md={8.5} lg={9} xl={10}>
            <Stack direction='column'>
              {!ttdom && !!governingStandardProcessDocs?.length && (
                <Box mb={2}>
                  <Typography variant='h5' mb={2}>
                    {newDocumentCategories.GoverningStandardProcess}
                  </Typography>
                  <Grid container rowGap='10px' columnGap='20px'>
                    {governingStandardProcessDocs.map(({artifact}, index) => (
                      <Grid item key={index} xs='auto'>
                        {storybook ? (
                          <ArtifactCard
                            data={{
                              ...artifact,
                              parent_process_id: process.process_id,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                            pinned={true}
                          />
                        ) : (
                          <ArtifactCards
                            data={{
                              ...artifact,
                              parent_process_id: process.process_id,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {!ttdom && !!keyProcessGxPDocs?.length && (
                <Box my={2}>
                  <Typography variant='h5' mb={2}>
                    {newDocumentCategories.KeyProcessGxP}
                  </Typography>
                  <Grid container rowGap='10px' columnGap='20px'>
                    {keyProcessGxPDocs.map(({artifact}, index) => (
                      <Grid item key={index} xs='auto'>
                        {storybook ? (
                          <ArtifactCard
                            data={{
                              ...artifact,
                              parent_process_id: process.process_id,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                            pinned={true}
                          />
                        ) : (
                          <ArtifactCards
                            data={{
                              ...artifact,
                              parent_process_id: process.process_id,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {!ttdom && !!keyProcessBusinessControlledDocs?.length && (
                <Box my={2}>
                  <Stack spacing={1} direction='row' alignItems='center' mb={2}>
                    <Typography variant='h5'>{newDocumentCategories.KeyProcessBusinessControlled}</Typography>
                  </Stack>
                  <Grid container rowGap='10px' columnGap='20px'>
                    {keyProcessBusinessControlledDocs.map(({artifact}, index) => (
                      <Grid item key={index} xs='auto'>
                        {storybook ? (
                          <ArtifactCard
                            data={{
                              ...artifact,
                              parent_process_id: process.process_id,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                            pinned={true}
                          />
                        ) : (
                          <ArtifactCards
                            data={{
                              ...artifact,
                              parent_process_id: process.process_id,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {!!referencedDocs?.length && (
                <Box my={2}>
                  <Stack spacing={1} direction='row' alignItems='center' mb={2}>
                    <Typography variant='h5'>{newDocumentCategories.Referenced}</Typography>
                  </Stack>
                  <Grid container rowGap='10px' columnGap='20px'>
                    {referencedDocs.map(({artifact}, index) => (
                      <Grid item key={index} xs='auto'>
                        {storybook ? (
                          <ArtifactCard
                            data={{
                              ...artifact,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                            pinned={true}
                          />
                        ) : (
                          <ArtifactCards
                            data={{
                              ...artifact,
                              created_dt: artifact.created_dt.substring(0, 10), // truncate to date
                              card: CardType.document,
                            }}
                          />
                        )}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Stack>
          </Grid>
          <Grid item xs={2.5} sm={4} md={3.5} lg={3} xl={2}>
            <IconKey items={getItemKey()} />
          </Grid>
        </Grid>
      ) : (
        <Box width='100%' height='15vw' display='flex' alignItems='center' justifyContent='center'>
          <EmptyState
            icon={<Close color='primary' fontSize='medium' />}
            title={`There are currently no resources available for ${process.name}`}
          />
        </Box>
      )}
    </Paper>
  );
};

export default DocAndTemplatesTab;
